import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Default currency decimals used by most currencies.
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision.
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * A custom element that renders a "frequently bought together" bundle: the product being
 * viewed plus a random selection of products pulled from a chosen collection. Shoppers can
 * toggle items on and off before adding every selected item to the cart at once.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} itemList
 * @property {HTMLButtonElement} claimButton
 * @property {HTMLElement} totalPrice
 * @property {HTMLElement} [totalComparePrice]
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLElement} [errorMessage]
 *
 * @extends {Component<Refs>}
 */
export class FrequentlyBoughtTogetherComponent extends Component {
  requiredRefs = ['itemList', 'claimButton', 'totalPrice'];

  connectedCallback() {
    super.connectedCallback();

    this.#revealRandomPoolItems();
    this.recalculateTotals();
  }

  /**
   * Handles a checkbox being toggled on an item row.
   */
  handleItemToggle() {
    this.recalculateTotals();
  }

  /**
   * Randomly picks which pool items (products pulled from the chosen collection) are shown
   * alongside the locked/main item. Falls back to the first N pool items (already visible via
   * the server-rendered markup) when JavaScript hasn't run yet.
   */
  #revealRandomPoolItems() {
    const upsellCount = Number(this.dataset.upsellCount) || 2;
    const poolItems = Array.from(this.querySelectorAll('[data-fbt-item][data-pool-index]'));

    if (poolItems.length === 0) return;

    for (let i = poolItems.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = poolItems[i];
      poolItems[i] = poolItems[j];
      poolItems[j] = temp;
    }

    poolItems.forEach((item, index) => {
      item.hidden = index >= upsellCount;
    });
  }

  /**
   * Gets every visible item row that is currently checked.
   * @returns {HTMLElement[]}
   */
  #getSelectedItems() {
    return Array.from(this.querySelectorAll('[data-fbt-item]')).filter((item) => {
      if (item.hidden) return false;

      const checkbox = item.querySelector('[data-fbt-checkbox]');
      return checkbox instanceof HTMLInputElement && checkbox.checked;
    });
  }

  /**
   * Recalculates the selected totals and updates the claim button.
   */
  recalculateTotals() {
    const selectedItems = this.#getSelectedItems();

    let total = 0;
    let compareTotal = 0;

    for (const item of selectedItems) {
      const price = parseInt(item.dataset.price || '0', 10);
      const compareAtPrice = parseInt(item.dataset.compareAtPrice || '0', 10) || price;

      total += price;
      compareTotal += compareAtPrice;
    }

    const { totalPrice, totalComparePrice, claimButton } = this.refs;

    totalPrice.textContent = this.#formatMoney(total);

    if (totalComparePrice) {
      if (compareTotal > total) {
        totalComparePrice.textContent = this.#formatMoney(compareTotal);
        totalComparePrice.hidden = false;
      } else {
        totalComparePrice.hidden = true;
      }
    }

    claimButton.disabled = selectedItems.length === 0;
  }

  /**
   * Adds every selected item to the cart in a single request.
   * @param {MouseEvent} event
   */
  async handleClaimOffer(event) {
    event.preventDefault();

    const selectedItems = this.#getSelectedItems();
    if (selectedItems.length === 0) return;

    const items = selectedItems.map((item) => ({
      id: Number(item.dataset.variantId),
      quantity: 1,
    }));

    const { claimButton } = this.refs;
    claimButton.disabled = true;
    claimButton.classList.add('fbt__bar--loading');
    this.#setError('');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((cartItems) => {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.push(cartItems.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        ...fetchConfig('json', { body }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        this.#setError(result.message || result.description || 'Something went wrong. Please try again.');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(
          {},
          this.id || 'frequently-bought-together',
          {
            source: 'frequently-bought-together',
            itemCount: items.length,
            sections: result.sections,
          }
        )
      );
    } catch (error) {
      console.error(error);
      this.#setError('Something went wrong. Please try again.');
    } finally {
      claimButton.classList.remove('fbt__bar--loading');
      this.recalculateTotals();
    }
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    errorMessage.textContent = message;
    errorMessage.hidden = message === '';
  }

  /**
   * Formats a money value (in cents) using the shop's configured money format.
   * Mirrors the output of Liquid's `money` filter.
   * @param {number} moneyValue
   * @returns {string}
   */
  #formatMoney(moneyValue) {
    const { moneyFormat } = this.refs;
    if (!(moneyFormat instanceof HTMLTemplateElement)) return moneyValue.toString();

    const template = moneyFormat.content.textContent || '{{amount}}';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return this.dataset.currency || '';

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[(this.dataset.currency || '').toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * @param {number} moneyValue - The money value in cents.
   * @param {string} thousandsSeparator
   * @param {string} decimalSeparator
   * @param {number} precision
   * @returns {string}
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [whole, fraction] = roundedNumber.split('.');
    if (!whole) whole = '0';
    if (!fraction) fraction = '';

    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
  }
}

if (!customElements.get('frequently-bought-together-component')) {
  customElements.define('frequently-bought-together-component', FrequentlyBoughtTogetherComponent);
}
